import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 过站检查单的时间一律按 UTC 存储（ISO8601, Z 后缀），界面按过站机场当地时区显示与录入。
 * 取不到机场时区时退回本机时区。本模块集中处理 UTC ⇄ 当地时间的换算。
 */

/** 解析实际使用的时区：优先机场时区，取不到时退回本机时区。 */
export const resolveTz = (headerTz) => {
    if (headerTz) return headerTz;
    try {
        return dayjs.tz.guess();
    } catch {
        return "UTC";
    }
};

/** 是否退回到了本机时区（机场时区取不到）。用于在表头标出。 */
export const isFallbackTz = (headerTz) => !headerTz;

/** 当前时刻的 UTC ISO 字符串（秒级，去毫秒，与后端 _now_iso 一致）。 */
export const nowUtcIso = () => new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

const safeTz = (utcIso, tzId) => {
    if (!utcIso) return null;
    const base = dayjs.utc(utcIso);
    if (!base.isValid()) return null;
    try {
        return base.tz(tzId);
    } catch {
        return base;
    }
};

/** UTC ISO → 当地 HH:mm（空值返回空串）。 */
export const utcToLocalHm = (utcIso, tzId) => {
    const local = safeTz(utcIso, tzId);
    return local ? local.format("HH:mm") : "";
};

/** UTC ISO → 当地 MM-DD HH:mm（跨日时也能看清是哪天）。 */
export const utcToLocalDateTime = (utcIso, tzId, fmt = "MM-DD HH:mm") => {
    const local = safeTz(utcIso, tzId);
    return local ? local.format(fmt) : "";
};

/** UTC ISO → 当地 dayjs 对象，供 antd TimePicker 作为受控 value。 */
export const utcToLocalDayjs = (utcIso, tzId) => safeTz(utcIso, tzId);

/**
 * 取这班的"当地锚定日期"（YYYY-MM-DD）：手动录入的 HH:mm 以此为日期基准。
 * 优先用航段 STD 换算成当地日期，取不到时退回当地今天。
 */
export const anchorLocalDate = (referenceUtcIso, tzId) => {
    const local = safeTz(referenceUtcIso, tzId);
    if (local) return local.format("YYYY-MM-DD");
    try {
        return dayjs().tz(tzId).format("YYYY-MM-DD");
    } catch {
        return dayjs().format("YYYY-MM-DD");
    }
};

/**
 * 当地选择的日期时间（DatePicker 选出的 dayjs）→ UTC ISO 字符串（秒级）。
 * 按 tzId 重新解释用户看到的"墙上时间"，避免受 picker 内部时区影响；时间项带日期，跨日才不会混。
 */
export const localDateTimeToUtcIso = (picked, tzId) => {
    if (!picked) return null;
    const wall = picked.format("YYYY-MM-DD HH:mm");
    try {
        const local = dayjs.tz(wall, tzId);
        if (!local.isValid()) return null;
        return local.utc().toISOString().replace(/\.\d{3}Z$/, "Z");
    } catch {
        const local = dayjs(wall);
        return local.isValid() ? local.utc().toISOString().replace(/\.\d{3}Z$/, "Z") : null;
    }
};

/** 锚定日期（YYYY-MM-DD）→ 当地 dayjs：时间项未填时 DatePicker 默认定位到航班当天，少翻页。 */
export const anchorPickerDayjs = (anchorDate, tzId) => {
    if (!anchorDate) return null;
    try {
        const local = dayjs.tz(`${anchorDate} 00:00`, tzId);
        return local.isValid() ? local : null;
    } catch {
        const local = dayjs(`${anchorDate} 00:00`);
        return local.isValid() ? local : null;
    }
};

/**
 * UTC ISO → 原生 Date（其年月日时分为过站机场当地"墙上时间"），供 antd-mobile DatePicker 受控 value。
 * antd-mobile 用原生 Date、按设备本地时区显示滚轮，这里把当地墙上数字塞进一个本地 Date，使滚轮显示正确。
 */
export const utcIsoToWallDate = (utcIso, tzId) => {
    const local = safeTz(utcIso, tzId);
    if (!local) return null;
    return new Date(local.year(), local.month(), local.date(), local.hour(), local.minute(), 0, 0);
};

/** antd-mobile DatePicker onConfirm 的原生 Date（按设备本地读出即"墙上时间"）→ UTC ISO（秒级，按过站机场时区解释）。 */
export const wallDateToUtcIso = (date, tzId) => {
    if (!date) return null;
    const pad = (n) => String(n).padStart(2, "0");
    const wall = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    try {
        const local = dayjs.tz(wall, tzId);
        if (!local.isValid()) return null;
        return local.utc().toISOString().replace(/\.\d{3}Z$/, "Z");
    } catch {
        const local = dayjs(wall);
        return local.isValid() ? local.utc().toISOString().replace(/\.\d{3}Z$/, "Z") : null;
    }
};

/** 锚定日期（YYYY-MM-DD）→ 原生 Date（当地当天 12:00）：时间项未填时滚轮默认定位航班当天，少滚。 */
export const anchorWallDate = (anchorDate) => {
    if (!anchorDate) return new Date();
    const [y, m, d] = String(anchorDate).split("-").map((v) => parseInt(v, 10));
    if (!y || !m || !d) return new Date();
    return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const rampChecklistTime = {
    resolveTz,
    isFallbackTz,
    nowUtcIso,
    utcToLocalHm,
    utcToLocalDateTime,
    utcToLocalDayjs,
    anchorLocalDate,
    localDateTimeToUtcIso,
    anchorPickerDayjs,
    utcIsoToWallDate,
    wallDateToUtcIso,
    anchorWallDate,
};

export default rampChecklistTime;
